import React, { useEffect, useMemo, useState } from "react";
import "./BusinessPhotoGallery.css";

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const uniquePhotos = (values) => {
  const output = [];
  const seen = new Set();

  values.flat(Infinity).forEach((value) => {
    const clean = firstText(value);
    if (!clean) return;

    const key = clean.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    output.push(clean);
  });

  return output;
};

export const normalizeBusinessPhotos = (business = {}, details = {}) => {
  const rawPhotos = [
    business?.photos,
    business?.photo_urls,
    business?.photoUrls,
    details?.photos,
    details?.photo_urls,
    details?.photoUrls,
  ].filter(Boolean);

  const coverPhoto = firstText(
    business?.cover_photo,
    business?.coverPhoto,
    details?.cover_photo,
    details?.coverPhoto
  );

  const photos = uniquePhotos([
    coverPhoto,
    ...rawPhotos,
  ]).slice(0, 4);

  return {
    photos,
    coverPhoto: coverPhoto || photos[0] || "",
  };
};

export default function BusinessPhotoGallery({
  photos = [],
  coverPhoto = "",
  businessName = "Business",
  compact = false,
}) {
  const normalizedPhotos = useMemo(
    () => uniquePhotos([coverPhoto, photos]).slice(0, 4),
    [coverPhoto, photos]
  );

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [normalizedPhotos.join("|")]);

  if (!normalizedPhotos.length) {
    return null;
  }

  const activePhoto =
    normalizedPhotos[Math.min(activeIndex, normalizedPhotos.length - 1)];

  return (
    <section
      className={`business-photo-gallery ${
        compact ? "business-photo-gallery--compact" : ""
      }`}
      aria-label={`${businessName} photos`}
    >
      <div className="business-photo-gallery__main">
        <img
          src={activePhoto}
          alt={`${businessName} photo ${activeIndex + 1}`}
          loading="lazy"
        />

        {normalizedPhotos.length > 1 && (
          <span className="business-photo-gallery__count">
            {activeIndex + 1}/{normalizedPhotos.length}
          </span>
        )}
      </div>

      {normalizedPhotos.length > 1 && (
        <div className="business-photo-gallery__thumbs">
          {normalizedPhotos.map((photo, index) => (
            <button
              key={`${photo}-${index}`}
              type="button"
              className={`business-photo-gallery__thumb ${
                index === activeIndex ? "is-active" : ""
              }`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show ${businessName} photo ${index + 1}`}
              aria-pressed={index === activeIndex}
            >
              <img
                src={photo}
                alt=""
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
